import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { departmentAPI, facilityAPI } from '../../services/api';
import RichContent from '../../components/public/RichContent';
import { phoneHref } from '../../utils/phone';
import './DepartmentDetailPage.css';

const META = {
  health: { label: 'आरोग्य विभाग', icon: 'heart', color: '#00a786', bg: '#e7fbf5', hero: '#0b826d', heroBg: '/HeaderBgImages/10.png' },
  agriculture: { label: 'कृषी विभाग', icon: 'tractor', color: '#76a82a', bg: '#eff9e6', hero: '#547d1c', heroBg: '/HeaderBgImages/11.png' },
  water_and_sanitation: { label: 'पाणीपुरवठा विभाग', icon: 'drop', color: '#1c92ed', bg: '#eaf6ff', hero: '#0c72b5', heroBg: '/HeaderBgImages/7.png' },
  pwd: { label: 'सार्वजनिक बांधकाम विभाग', icon: 'road', color: '#d97736', bg: '#fff1e8', hero: '#b45b1f', heroBg: '/HeaderBgImages/6.png' },
  education: { label: 'शिक्षण विभाग', icon: 'cap', color: '#8e44ad', bg: '#f5eafe', hero: '#71328d', heroBg: '/HeaderBgImages/3.png' },
  women_child: { label: 'महिला व बाल विकास विभाग', icon: 'family', color: '#e43f73', bg: '#fff0f5', hero: '#bd2d5a', heroBg: '/HeaderBgImages/14.png' },
  social_welfare: { label: 'सामाजिक न्याय विभाग', icon: 'group', color: '#259b51', bg: '#eaf8ef', hero: '#16743b', heroBg: '/HeaderBgImages/14.png' },
  finance: { label: 'वित्त व लेखा विभाग', icon: 'coins', color: '#9b59b6', bg: '#f4eafd', hero: '#70379a', heroBg: '/HeaderBgImages/5.png' },
  administration: { label: 'प्रशासकीय विभाग', icon: 'building', color: '#2563eb', bg: '#edf4ff', hero: '#0b62ca', heroBg: '/HeaderBgImages/12.png' },
  animal_husbandry: { label: 'पशुसंवर्धन विभाग', icon: 'heart', color: '#0f9f8f', bg: '#e6fbf7', hero: '#0a736a', heroBg: '/HeaderBgImages/10.png' },
  rural_development: { label: 'ग्रामविकास विभाग', icon: 'home', color: '#f59e0b', bg: '#fff6de', hero: '#bc7900', heroBg: '/HeaderBgImages/8.png' },
  tourism: { label: 'पर्यटन विभाग', icon: 'map', color: '#0ea5e9', bg: '#e8f8ff', hero: '#057aa8', heroBg: '/HeaderBgImages/9.png' },
  other: { label: 'विभाग', icon: 'grid', color: '#64748b', bg: '#eef2f6', hero: '#475569', heroBg: '/HeaderBgImages/2.png' },
};

const DOC_LABELS = {
  aadhar: 'आधार कार्ड',
  pan: 'पॅन कार्ड',
  passport: 'पासपोर्ट',
  voter_id: 'मतदार ओळखपत्र',
  driving_license: 'वाहन परवाना',
  property_document: 'मालमत्ता कागदपत्र',
  bank_passbook: 'बँक पासबुक',
  electricity_bill: 'वीज बिल',
  water_bill: 'पाणी बिल',
  tax_certificate: 'कर प्रमाणपत्र',
  caste_certificate: 'जात प्रमाणपत्र',
  income_certificate: 'उत्पन्न प्रमाणपत्र',
  business_license: 'व्यवसाय परवाना',
  agricultural_land_document: 'शेती जमीन कागदपत्र',
};

const DEPARTMENT_FACILITY_TYPES = {
  health: ['hospital', 'health_center', 'ambulance', 'emergency_service'],
  water_and_sanitation: ['water_supply'],
  education: ['educational'],
  tourism: ['tourism', 'religious_site'],
  agriculture: ['veterinary'],
  animal_husbandry: ['veterinary'],
  pwd: ['fire_station', 'other'],
  administration: ['other', 'police'],
};

const FACILITY_TYPE_LABELS = {
  hospital: 'अस्पताल',
  health_center: 'आरोग्य केंद्र',
  ambulance: 'रुग्णवाहिका सेवा',
  emergency_service: 'आपत्कालीन सेवा',
  tourism: 'पर्यटन स्थळ',
  religious_site: 'धार्मिक स्थळ',
  educational: 'शैक्षणिक सुविधा',
  water_supply: 'पाणीपुरवठा सुविधा',
  electricity: 'वीज सुविधा',
  fire_station: 'अग्निशमन सेवा',
  police: 'पोलीस सेवा',
  veterinary: 'पशुवैद्यकीय सुविधा',
  other: 'इतर सुविधा',
};

function Icon({ name, size = 24 }) {
  const paths = {
    heart: <><path d="M20.8 4.6a5.4 5.4 0 0 0-7.6 0L12 5.8l-1.2-1.2a5.4 5.4 0 1 0-7.6 7.6L12 21l8.8-8.8a5.4 5.4 0 0 0 0-7.6Z" /><path d="M7 12h3l1-3 2 6 1-3h3" /></>,
    tractor: <><path d="M7 14V6h7l3 5h2" /><circle cx="7" cy="17" r="3" /><circle cx="18" cy="17" r="2" /><path d="M10 17h6M10 9h5" /></>,
    drop: <path d="M12 3s7 7.1 7 12a7 7 0 0 1-14 0c0-4.9 7-12 7-12Z" />,
    road: <><path d="M7 21 11 3h2l4 18" /><path d="M12 7v2M12 13v2M5 21h14" /></>,
    cap: <><path d="m3 8 9-5 9 5-9 5-9-5Z" /><path d="M7 10v5c3 2 7 2 10 0v-5" /></>,
    family: <><circle cx="9" cy="7" r="3" /><circle cx="17" cy="8" r="2.5" /><path d="M3 21a6 6 0 0 1 12 0M14 21a5 5 0 0 1 7 0" /></>,
    group: <><circle cx="8" cy="8" r="3" /><circle cx="16" cy="8" r="3" /><path d="M2 21a6 6 0 0 1 12 0M10 21a6 6 0 0 1 12 0" /></>,
    coins: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></>,
    building: <><path d="M4 21V7l8-4 8 4v14" /><path d="M9 21v-6h6v6M8 9h.01M12 9h.01M16 9h.01M8 13h.01M16 13h.01" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v11h14V10M9 21v-6h6v6" /></>,
    map: <><path d="m9 18-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" /><path d="M9 3v15M15 6v15" /></>,
    grid: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    user: <><circle cx="12" cy="7" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    phone: <><path d="M6.5 3h3l1.5 5-2 1.5a15 15 0 0 0 5.5 5.5l1.5-2 5 1.5v3A3.5 3.5 0 0 1 17.5 21C9.5 21 3 14.5 3 6.5A3.5 3.5 0 0 1 6.5 3Z" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6l4 2" /></>,
    file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    back: <path d="M19 12H5M11 5l-7 7 7 7" />,
    image: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>,
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
    close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  };
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.grid}</svg>;
}

const metaFor = type => META[type] || META.other;
const titleFor = dept => dept?.name || dept?.name_english || 'विभाग';
const splitList = value => Array.isArray(value) ? value.filter(Boolean) : String(value || '').split(/\r?\n|,/).map(item => item.trim()).filter(Boolean);
const parseDetails = value => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};
const parseSections = value => {
  if (!value) return [];
  let sections = value;
  if (typeof value === 'string') {
    try {
      sections = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(sections)) return [];
  return sections
    .filter(section => section && (section.heading || section.content || section.image_url))
    .map((section, index) => ({ ...section, order: Number(section.order ?? index) }))
    .sort((a, b) => a.order - b.order);
};
const documents = value => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(item => DOC_LABELS[item] || item);
  if (typeof value === 'object') return Object.values(value).flat().map(item => DOC_LABELS[item] || item);
  return splitList(value).map(item => DOC_LABELS[item] || item);
};

const parseImages = (value) => {
  if (!value) return [];
  let items = value;
  if (typeof value === 'string') {
    try {
      items = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    if (typeof item === 'string') {
      return { id: `img-${index}`, url: item.trim(), caption: '' };
    }
    return {
      id: item?.id || `img-${index}`,
      url: String(item?.url || item?.image_url || '').trim(),
      caption: String(item?.caption || item?.image_caption || '').trim(),
    };
  }).filter(item => item && item.url);
};

function DepartmentCarousel({ images, departmentName }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const total = images.length;

  useEffect(() => {
    if (total <= 1 || isPaused || lightboxOpen) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % total);
    }, 4500);
    return () => clearInterval(interval);
  }, [total, isPaused, lightboxOpen]);

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + total) % total);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % total);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') handlePrev(e);
    if (e.key === 'ArrowRight') handleNext(e);
    if (e.key === 'Escape') setLightboxOpen(false);
  };

  if (!images || total === 0) return null;

  const currentImage = images[currentIndex] || images[0];

  return (
    <section
      id="gallery"
      className="dept-carousel-card"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-label="Department Image Gallery"
    >
      <div
        className="dept-carousel-stage"
        onClick={() => setLightboxOpen(true)}
        role="button"
        tabIndex={0}
        aria-label="View image full size"
      >
        {images.map((img, idx) => (
          <div
            key={img.id || idx}
            className={`dept-carousel-slide ${idx === currentIndex ? 'active' : ''}`}
            aria-hidden={idx !== currentIndex}
          >
            <img src={img.url} alt={img.caption || `${departmentName} Image ${idx + 1}`} />
          </div>
        ))}

        <div className="dept-carousel-counter">
          {currentIndex + 1} / {total}
        </div>

        <div className="dept-carousel-overlay">
          {currentImage.caption ? (
            <div className="dept-carousel-caption">{currentImage.caption}</div>
          ) : (
            <div className="dept-carousel-caption">{departmentName} छायाचित्रे</div>
          )}
        </div>
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            className="dept-carousel-btn prev"
            onClick={handlePrev}
            aria-label="मागील छायाचित्र"
          >
            <Icon name="chevronLeft" size={20} />
          </button>
          <button
            type="button"
            className="dept-carousel-btn next"
            onClick={handleNext}
            aria-label="पुढील छायाचित्र"
          >
            <Icon name="chevronRight" size={20} />
          </button>

          <div className="dept-carousel-dots" role="tablist">
            {images.map((_, idx) => (
              <button
                type="button"
                key={idx}
                className={`dept-carousel-dot ${idx === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
                aria-label={`Slide ${idx + 1}`}
                role="tab"
                aria-selected={idx === currentIndex}
              />
            ))}
          </div>
        </>
      )}

      {lightboxOpen && (
        <div
          className="dept-lightbox-backdrop"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="dept-lightbox-content" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="dept-lightbox-close"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close"
            >
              <Icon name="close" size={20} />
            </button>
            <img
              src={currentImage.url}
              alt={currentImage.caption || `${departmentName} Image`}
            />
            {currentImage.caption && (
              <div className="dept-lightbox-caption">{currentImage.caption}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function SectionCard({ id, icon, title, children }) {
  return (
    <section id={id} className="dept-detail-card">
      <h2><Icon name={icon} size={24} />{title}</h2>
      {children}
    </section>
  );
}

export default function DepartmentDetailPage() {
  const { tenantSlug, departmentId } = useParams();
  const navigate = useNavigate();
  const { tenantData } = useTenant();
  const [department, setDepartment] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      departmentAPI.getById(tenantSlug, departmentId),
      facilityAPI.getAll(tenantSlug).catch(() => ({ data: [] })),
    ])
      .then(([departmentRes, facilitiesRes]) => {
        setDepartment(departmentRes.data);
        setFacilities(Array.isArray(facilitiesRes.data) ? facilitiesRes.data : []);
      })
      .catch(() => setError('विभागाची माहिती लोड करता आली नाही.'))
      .finally(() => setLoading(false));
  }, [tenantSlug, departmentId]);

  const meta = metaFor(department?.department_type);
  const services = useMemo(() => splitList(department?.services_offered), [department]);
  const docs = useMemo(() => documents(department?.documents_required), [department]);
  const staff = department?.StaffMembers || [];
  const contentSections = useMemo(() => parseSections(department?.content_sections), [department]);
  const images = useMemo(() => parseImages(department?.images), [department]);
  const relatedFacilities = useMemo(() => {
    const allowedTypes = DEPARTMENT_FACILITY_TYPES[department?.department_type] || [];
    if (!allowedTypes.length) return [];
    return facilities.filter(facility => allowedTypes.includes(facility.facility_type));
  }, [department, facilities]);
  const facilityServices = useMemo(() => relatedFacilities.flatMap(facility => {
    const details = parseDetails(facility.additional_details);
    return Array.isArray(details.services) ? details.services : [];
  }), [relatedFacilities]);
  const officePhone = department?.office_phone || department?.head_phone || tenantData?.tenant?.contact_phone;
  const officeEmail = department?.office_email || department?.head_email || tenantData?.tenant?.contact_email;
  const officeHours = department?.office_hours || tenantData?.tenant?.office_hours || '10:00 AM - 05:00 PM';

  if (loading) return <div className="dept-detail-state">विभागाची माहिती लोड होत आहे...</div>;
  if (error) return <div className="dept-detail-state error">{error}</div>;
  if (!department) return <div className="dept-detail-state">विभाग उपलब्ध नाही.</div>;

  return (
    <div className="department-detail-page" style={{ '--dept-color': meta.color, '--dept-bg': meta.bg, '--dept-hero': meta.hero, '--dept-hero-bg': `url("${meta.heroBg}")` }}>
      <section className="dept-detail-hero">
        <div className="dept-hero-copy">
          <h1>{titleFor(department)}</h1>
          <p>{department.description || 'ग्रामपंचायत अंतर्गत विभागीय सेवा, योजना आणि उपक्रमांची माहिती.'}</p>
          <button type="button" onClick={() => navigate(-1)}><Icon name="back" size={16} /> मागे जा</button>
        </div>
      </section>

      <div className="dept-detail-layout">
        <aside className="dept-detail-menu">
          <h2>विभागाची माहिती</h2>
          {[            
            ['overview', 'heart', `${meta.label} माहिती`],
            ...(images.length ? [['gallery', 'image', 'छायाचित्रे']] : []),
            ...(contentSections.length ? [['details', 'file', 'सविस्तर माहिती']] : []),
            ['services', 'check', 'सेवा व कार्यक्रम'],
            ['facilities', 'building', 'प्राथमिक सुविधा'],
            ['documents', 'file', 'आवश्यक कागदपत्रे'],
            ['staff', 'group', 'कर्मचारी'],
            ['contact', 'phone', 'संपर्क माहिती'],
          ].map(([id, icon, label]) => <a key={id} href={`#${id}`}><Icon name={icon} size={18} />{label}</a>)}
        </aside>

        <main className="dept-detail-main">
          {images.length > 0 && (
            <DepartmentCarousel images={images} departmentName={titleFor(department)} />
          )}

          <SectionCard id="overview" icon={meta.icon} title={`${meta.label} माहिती`}>
            <p className="dept-lead">{department.description || 'या विभागाची सविस्तर माहिती अद्याप उपलब्ध नाही.'}</p>
            <div className="dept-info-grid">
              <div><span>विभाग प्रकार</span><strong>{meta.label}</strong></div>
              <div><span>विभाग प्रमुख</span><strong>{department.head_name || 'माहिती उपलब्ध नाही'}</strong></div>
              <div><span>पद</span><strong>{department.head_designation || 'माहिती उपलब्ध नाही'}</strong></div>
              <div><span>कार्यालय</span><strong>{department.office_location || department.office_address || 'ग्रामपंचायत कार्यालय'}</strong></div>
            </div>
          </SectionCard>

          {contentSections.length > 0 && (
            <div id="details" className="dept-rich-section-list">
              {contentSections.map((section, index) => (
                <section className="dept-detail-card dept-rich-section" key={section.id || `${section.heading}-${index}`}>
                  {section.heading && <h2><Icon name={section.icon || meta.icon} size={24} />{section.heading}</h2>}
                  {section.image_url && (
                    <figure>
                      <img src={section.image_url} alt={section.image_alt || section.heading || ''} />
                      {section.image_caption && <figcaption>{section.image_caption}</figcaption>}
                    </figure>
                  )}
                  {section.content && <RichContent html={section.content} className="dept-rich-content" />}
                </section>
              ))}
            </div>
          )}

          <SectionCard id="services" icon="check" title="सेवा व कार्यक्रम">
            {services.length || facilityServices.length ? (
              <div className="dept-service-table">
                {(services.length ? services : facilityServices).map((item, index) => (
                  <div key={`${item}-${index}`}><span>{index + 1}</span><strong>{item}</strong><em>उपलब्ध</em></div>
                ))}
              </div>
            ) : (
              <p className="dept-empty">या विभागासाठी सेवा / कार्यक्रम अद्याप नोंदवलेले नाहीत.</p>
            )}
          </SectionCard>

          <SectionCard id="facilities" icon="building" title={`${meta.label} प्राथमिक सुविधा`}>
            {relatedFacilities.length ? (
              <>
                <div className="dept-facility-table">
                  <div className="dept-facility-head"><strong>सुविधेचा प्रकार</strong><strong>तपशील / उपलब्ध सेवा</strong><strong>संपर्क</strong></div>
                  {relatedFacilities.map(facility => {
                    const details = parseDetails(facility.additional_details);
                    const detailsList = [
                      facility.description,
                      Array.isArray(details.services) ? `सेवा: ${details.services.join(', ')}` : '',
                      details.beds ? `बेड: ${details.beds}` : '',
                      details.doctors ? `डॉक्टर: ${details.doctors}` : '',
                      facility.opening_hours ? `वेळ: ${facility.opening_hours}` : '',
                      facility.availability ? `उपलब्धता: ${facility.availability}` : '',
                    ].filter(Boolean);
                    return (
                      <div className="dept-facility-row" key={facility.id}>
                        <span>{FACILITY_TYPE_LABELS[facility.facility_type] || facility.facility_type}</span>
                        <p><strong>{facility.name}</strong>{detailsList.map((item, index) => <em key={index}>{item}</em>)}</p>
                        {(facility.phone || facility.email) && (
                          <small>
                            {facility.phone && <a href={phoneHref(facility.phone)}>{facility.phone}</a>}
                            {facility.email && <a href={`mailto:${facility.email}`}>{facility.email}</a>}
                          </small>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Link className="dept-view-all-link" to={`/gp/${tenantSlug}/facilities`}>सर्व सुविधा पहा <Icon name="arrow" size={16} /></Link>
              </>
            ) : (
              <p className="dept-empty">या विभागाशी संबंधित सुविधा अद्याप नोंदवलेल्या नाहीत. सुविधा admin मधून जोडल्यावर त्या इथे दिसतील.</p>
            )}
          </SectionCard>

          <div className="dept-two-col">
            <SectionCard id="documents" icon="file" title="आवश्यक कागदपत्रे">
              {docs.length ? (
                <ul className="dept-doc-list">{docs.map((doc, index) => <li key={`${doc}-${index}`}><Icon name="file" size={17} />{doc}</li>)}</ul>
              ) : (
                <p className="dept-empty">कागदपत्रांची यादी उपलब्ध नाही.</p>
              )}
            </SectionCard>

            <SectionCard id="contact" icon="phone" title="संपर्क माहिती">
              <div className="dept-contact-list">
                <p><Icon name="user" size={18} /><span>{department.head_name || 'विभाग प्रमुख'}</span></p>
                {officePhone && <a href={phoneHref(officePhone)}><Icon name="phone" size={18} /><span>{officePhone}</span></a>}
                {officeEmail && <a href={`mailto:${officeEmail}`}><Icon name="mail" size={18} /><span>{officeEmail}</span></a>}
                <p><Icon name="clock" size={18} /><span>{officeHours}</span></p>
              </div>
            </SectionCard>
          </div>

          <SectionCard id="staff" icon="group" title={`${meta.label} कर्मचारी`}>
            {staff.length ? (
              <div className="dept-staff-table-wrap">
                <table className="dept-staff-table">
                  <thead><tr><th>क्र.</th><th>नाव</th><th>पद</th><th>कार्यालय</th><th>संपर्क क्रमांक</th></tr></thead>
                  <tbody>
                    {staff.map((member, index) => (
                      <tr key={member.id || `${member.full_name}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{member.full_name || '-'}</td>
                        <td>{member.designation_marathi || member.designation || '-'}</td>
                        <td>{member.office_location || department.office_location || '-'}</td>
                        <td>{member.phone ? <a href={phoneHref(member.phone)}>{member.phone}</a> : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="dept-empty">या विभागासाठी कर्मचारी माहिती उपलब्ध नाही.</p>
            )}
          </SectionCard>

          <section className="dept-help-banner">
            <Icon name={meta.icon} size={44} />
            <div>
              <h2>या विभागाबाबत मदत हवी आहे?</h2>
              <p>संबंधित सेवा, अर्ज किंवा तक्रारींसाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p>
            </div>
            {officePhone ? <a href={phoneHref(officePhone)}><Icon name="phone" size={20} /> {officePhone}</a> : <Link to={`/gp/${tenantSlug}/contact`}>संपर्क साधा <Icon name="arrow" size={16} /></Link>}
          </section>
        </main>
      </div>
    </div>
  );
}
